import { css } from 'aphrodite';
import * as React from 'react';
import { ContentCalendarSuggestionAccountAge, Industry } from '../../../../extViewmodels';
import { CampaignType } from '../../../../models/AdminModels';
import { getSubverticalOptions } from '../../../../models/UiUtils';
import { DeprecatedSelect, ISelectOption, MultiSelect } from '../../../../web/components/DeprecatedSelect';
import { useGhostCalendar } from './context';
import {
	GhostCalendarAccountAgeOptions,
	GhostCalendarAllIndustriesOption,
	GhostCalendarAllSubverticalsOption,
	GhostCalendarCampaignTypeOptions,
	GhostCalendarIndustryOptions,
} from './models';
import { styleSheet } from './styles';

const getSubverticalOptionsForIndustry = (industry: Industry) => {
	const subverticals = getSubverticalOptions(industry);
	return subverticals.map<ISelectOption<string>>(x => {
		return {
			dataContext: x,
			id: `subvertical-option-${x}`,
			text: x,
			type: 'checkbox',
		};
	});
};

export const GhostCalendarFilters: React.FC = () => {
	const { selectedFilterOptions, setSelectedFilterOptions } = useGhostCalendar();

	const subverticalOptions = React.useMemo<ISelectOption[]>(() => {
		const selectedIndustry = selectedFilterOptions.industries?.[0];
		return selectedIndustry?.dataContext ? getSubverticalOptionsForIndustry(selectedIndustry.dataContext) : [];
	}, [selectedFilterOptions]);

	const isSubverticalSelectionEnabled = selectedFilterOptions?.industries?.length === 1;
	const isAllSubverticalsSelected = selectedFilterOptions?.subverticals?.length === subverticalOptions.length;
	const isAllIndustriesSelected = selectedFilterOptions?.industries?.length === GhostCalendarIndustryOptions.length;

	// #region Industries/Verts.
	const onIndustryOptionClicked = React.useCallback(
		(option: ISelectOption, wasSelected: boolean) => {
			const nextSelectedIndustries = [...(selectedFilterOptions.industries || [])];
			const index = nextSelectedIndustries.findIndex(x => x.dataContext === option.dataContext);
			if (!!wasSelected && index < 0) {
				nextSelectedIndustries.push(option);
			} else if (!wasSelected && index >= 0) {
				nextSelectedIndustries.splice(index, 1);
			}

			setSelectedFilterOptions({
				...selectedFilterOptions,
				industries: nextSelectedIndustries,
				subverticals:
					nextSelectedIndustries.length === 1
						? getSubverticalOptionsForIndustry(nextSelectedIndustries[0].dataContext)
						: [],
			});
		},
		[selectedFilterOptions, setSelectedFilterOptions]
	);

	const onAllIndustriesClicked = () => {
		setSelectedFilterOptions({
			...selectedFilterOptions,
			industries: isAllIndustriesSelected ? [] : GhostCalendarIndustryOptions,
			subverticals: [],
		});
	};

	const onSelectSubvertical = React.useCallback(
		(option: ISelectOption<string>, wasSelected: boolean) => {
			const nextSelectedSubverticals = [...(selectedFilterOptions.subverticals || [])];
			const index = nextSelectedSubverticals.findIndex(x => x.dataContext === option.dataContext);
			if (!!wasSelected && index < 0) {
				nextSelectedSubverticals.push(option);
			} else if (!wasSelected && index >= 0) {
				nextSelectedSubverticals.splice(index, 1);
			}

			setSelectedFilterOptions({
				...selectedFilterOptions,
				subverticals: nextSelectedSubverticals,
			});
		},
		[selectedFilterOptions, setSelectedFilterOptions]
	);

	const onAllSubverticalsClicked = () => {
		setSelectedFilterOptions({
			...selectedFilterOptions,
			subverticals: isAllSubverticalsSelected ? [] : subverticalOptions,
		});
	};

	const onRenderIndustriesFilterTitle = React.useCallback(() => {
		const count = selectedFilterOptions.industries?.length || 0;
		if (count === GhostCalendarIndustryOptions.length) {
			return 'All industries';
		}
		if (count === 1) {
			return selectedFilterOptions.industries[0].text;
		}
		return `${count} industries`;
	}, [selectedFilterOptions.industries]);

	const onRenderSubverticalFilterTitle = React.useCallback(() => {
		const count = selectedFilterOptions.subverticals?.length || 0;
		if (!isSubverticalSelectionEnabled || count === subverticalOptions.length) {
			return 'All subverticals';
		}
		if (count === 1) {
			return selectedFilterOptions.subverticals[0].text;
		}
		return `${count} subverticals`;
	}, [selectedFilterOptions.subverticals, isSubverticalSelectionEnabled, subverticalOptions]);
	// #endregion

	// #region account age
	const [selectedAccountAge, setSelectedAccountAge] = React.useState<
		ISelectOption<ContentCalendarSuggestionAccountAge>
	>(
		selectedFilterOptions.ages?.length
			? GhostCalendarAccountAgeOptions.find(x => x.dataContext === selectedFilterOptions.ages[0].dataContext)
			: GhostCalendarAccountAgeOptions[0]
	);

	const [selectedCampaignType, setSelectedCampaignType] = React.useState<ISelectOption<CampaignType>>(
		GhostCalendarCampaignTypeOptions[0]
	);
	const onSelectAccountAge = React.useCallback(
		(option: ISelectOption<ContentCalendarSuggestionAccountAge>, wasSelected: boolean) => {
			if (wasSelected) {
				setSelectedAccountAge(option);
				setSelectedFilterOptions({
					...selectedFilterOptions,
					ages: !option.dataContext ? [] : [option],
				});
			}
		},
		[selectedFilterOptions, setSelectedFilterOptions]
	);
	// #endregion

	const onRenderAccountAgeTrigger = React.useCallback((option: ISelectOption<string>) => {
		return (
			<div className={css(styleSheet.triggerContainer)}>
				<span className={css(styleSheet.triggerValue)}>{option.text}</span>
			</div>
		);
	}, []);

	const onRenderCampaignTypeTrigger = React.useCallback((option: ISelectOption<string>) => {
		return (
			<div className={css(styleSheet.triggerContainer)}>
				<span className={css(styleSheet.triggerValue)}>{option.text}</span>
			</div>
		);
	}, []);

	const onSelectCampaignType = React.useCallback(
		(option: ISelectOption<CampaignType>, wasSelected: boolean) => {
			if (wasSelected) {
				setSelectedCampaignType(option);
				setSelectedFilterOptions({
					...selectedFilterOptions,
					campaignType: !option.dataContext ? [] : [option],
				});
			}
		},
		[selectedFilterOptions, setSelectedFilterOptions]
	);

	return (
		<div className={css(styleSheet.filtersContainer)}>
			<div className={css(styleSheet.row)}>
				<MultiSelect
					isAllSelected={isAllIndustriesSelected}
					onAllClicked={onAllIndustriesClicked}
					onOptionClick={onIndustryOptionClicked}
					options={GhostCalendarIndustryOptions}
					selectAllOption={GhostCalendarAllIndustriesOption}
					selectedOptions={selectedFilterOptions.industries}
					selectedOptionsTitle={onRenderIndustriesFilterTitle()}
					styles={[styleSheet.industrySelect]}
				/>
				<MultiSelect
					isAllSelected={isAllSubverticalsSelected}
					onAllClicked={onAllSubverticalsClicked}
					disabled={!isSubverticalSelectionEnabled}
					onOptionClick={onSelectSubvertical}
					options={subverticalOptions}
					selectedOptions={selectedFilterOptions.subverticals}
					selectedOptionsTitle={onRenderSubverticalFilterTitle()}
					styles={[styleSheet.subverticalSelect, !isSubverticalSelectionEnabled && styleSheet.selectDisabled]}
					selectAllOption={GhostCalendarAllSubverticalsOption}
				/>
			</div>
			<DeprecatedSelect
				options={GhostCalendarCampaignTypeOptions}
				selectedOptionTitle={onRenderCampaignTypeTrigger}
				selectedOption={selectedCampaignType}
				onOptionClick={onSelectCampaignType}
				styles={[styleSheet.accountAgeSelect]}
			/>
			<DeprecatedSelect
				options={GhostCalendarAccountAgeOptions}
				selectedOptionTitle={onRenderAccountAgeTrigger}
				selectedOption={selectedAccountAge}
				onOptionClick={onSelectAccountAge}
				styles={[styleSheet.accountAgeSelect]}
			/>
		</div>
	);
};
